import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {getSizeBucket} from './processProcessesEvents';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata} from './ProfilerConstants';

/*
 * Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_REQUESTS_V2_EVENTS_VERSION = 1;

export enum ProfilerRequestsV2Features {
  CREATE_REQUEST_V2_SUBMISSION = 'createRequestV2Submission',
  CREATE_REQUEST_V2_DEFINITION = 'createRequestV2Definition',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerRequestsV2Features]: Function} = {
  [ProfilerRequestsV2Features.CREATE_REQUEST_V2_SUBMISSION]: processCreateRequestV2Submission,
  [ProfilerRequestsV2Features.CREATE_REQUEST_V2_DEFINITION]: processCreateRequestV2Definition,
};

enum ProfilerRequestsV2Actions {
  CREATE_REQUEST_V2_SUBMISSION_STARTED = 'createRequestV2SubmissionStarted',
  CREATE_REQUEST_V2_SUBMISSION_ENDED = 'createRequestV2SubmissionEnded',

  CREATE_REQUEST_V2_DEFINITION_STARTED = 'createRequestV2DefinitionStarted',
  CREATE_REQUEST_V2_DEFINITION_ENDED = 'createRequestV2DefinitionEnded',
}

export const REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REQUESTS_V2,
  feature: ProfilerRequestsV2Features.CREATE_REQUEST_V2_SUBMISSION,
  action: ProfilerRequestsV2Actions.CREATE_REQUEST_V2_SUBMISSION_STARTED,
};

export const REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REQUESTS_V2,
  feature: ProfilerRequestsV2Features.CREATE_REQUEST_V2_SUBMISSION,
  action: ProfilerRequestsV2Actions.CREATE_REQUEST_V2_SUBMISSION_ENDED,
};

export const REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REQUESTS_V2,
  feature: ProfilerRequestsV2Features.CREATE_REQUEST_V2_DEFINITION,
  action: ProfilerRequestsV2Actions.CREATE_REQUEST_V2_DEFINITION_STARTED,
};

export const REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REQUESTS_V2,
  feature: ProfilerRequestsV2Features.CREATE_REQUEST_V2_DEFINITION,
  action: ProfilerRequestsV2Actions.CREATE_REQUEST_V2_DEFINITION_ENDED,
};

export function processRequestsV2Events(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerRequestsV2Features;
  assert(
    Object.values(ProfilerRequestsV2Features).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

export function createRequestV2EventMetadata(actionOrigin: ActionOrigin): EventMetadata {
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations
  const metadata: EventMetadata = {
    distributionMetadata: {},
    actionMetadata: {},
  };

  const coercedActionOrigin = toDatadogTagFormat(actionOrigin);

  metadata.distributionMetadata.action_origin = coercedActionOrigin;
  metadata.actionMetadata.action_origin = coercedActionOrigin;

  return metadata;
}

export function createRequestV2SubmissionEventMetadata(
  actionOrigin: ActionOrigin,
  numSeedTasks: number
): EventMetadata {
  const metadata = createRequestV2EventMetadata(actionOrigin);
  metadata.distributionMetadata.num_seed_tasks = getSizeBucket(numSeedTasks);
  metadata.actionMetadata.num_seed_tasks = getSizeBucket(numSeedTasks);
  return metadata;
}

function processCreateRequestV2Submission(): void {
  processTimingEvent(
    REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_STARTED_EVENT,
    REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_ENDED_EVENT,
    'Requests V2 Create Request V2 Submission',
    'perf.create_request_v2_submission',
    PROFILER_REQUESTS_V2_EVENTS_VERSION
  );
}

function processCreateRequestV2Definition(): void {
  processTimingEvent(
    REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_STARTED_EVENT,
    REQUESTS_V2_CREATE_REQUEST_V2_DEFINITION_ENDED_EVENT,
    'Requests V2 Create Request V2 Definition',
    'perf.create_request_v2_definition',
    PROFILER_REQUESTS_V2_EVENTS_VERSION
  );
}
